// Copyright © 2021 The Things Industries B.V.

import React from 'react'
import { defineMessages } from 'react-intl'
import { useSelector } from 'react-redux'

import AWSIoTImage from '@assets/misc/aws-iot-logo.png'

import PageTitle from '@ttn-lw/components/page-title'
import Breadcrumb from '@ttn-lw/components/breadcrumbs/breadcrumb'
import { useBreadcrumbs } from '@ttn-lw/components/breadcrumbs/context'
import Link from '@ttn-lw/components/link'

import Message from '@ttn-lw/lib/components/message'
import ErrorView from '@ttn-lw/lib/components/error-view'

import AWSIoTIntegrationForm from '@console/containers/aws-iot-integration-form/index.tti'

import Require from '@console/lib/components/require'

import SubViewError from '@console/views/sub-view-error'

import sharedMessages from '@ttn-lw/lib/shared-messages'

import { mayViewOrEditApplicationPackages } from '@console/lib/feature-checks'

import { selectSelectedApplicationId } from '@console/store/selectors/applications'

import style from './application-integrations-aws-iot.tti.styl'

const m = defineMessages({
  awsIoTDefaultIntegration: 'AWS IoT Default Integration',
  awsIoTIntegration: 'AWS IoT integration',
  deploymentGuide: 'Deployment Guide',
  infoText: `The AWS IoT integration supports streaming data, creating and claiming end devices, advanced security and metrics. This integration comes with an AWS CloudFormation template to deploy in your AWS account. Click Deployment Guide below to learn how to deploy the CloudFormation template.`,
})

const AWSIoTIntegration = () => {
  const appId = useSelector(selectSelectedApplicationId)
  useBreadcrumbs(
    'apps.single.integrations.aws-iot',
    <Breadcrumb
      path={`/applications/${appId}/integrations/aws-iot`}
      content={sharedMessages.awsIoT}
    />,
  )

  return (
    <Require
      featureCheck={mayViewOrEditApplicationPackages}
      otherwise={{ redirect: `/applications/${appId}` }}
    >
      <ErrorView errorRender={SubViewError}>
        <div className="container container--xxl grid">
          <PageTitle title={m.awsIoTIntegration} />
          <div className="item-12 lg:item-8">
            <img className={style.logo} src={AWSIoTImage} alt="AWS IoT" />
            <Message content={m.infoText} className={style.info} />
            <div>
              <Message
                component="h4"
                content={sharedMessages.furtherResources}
                className={style.furtherResources}
              />
              <Link.DocLink path="/integrations/aws-iot/default/" secondary>
                <Message content={m.awsIoTDefaultIntegration} />
              </Link.DocLink>
              {' | '}
              <Link.DocLink path="/integrations/aws-iot/default/deployment-guide/" secondary>
                <Message content={m.deploymentGuide} />
              </Link.DocLink>
            </div>
            <hr className={style.hRule} />
            <Message component="h3" content={sharedMessages.configuration} />
            <AWSIoTIntegrationForm />
          </div>
        </div>
      </ErrorView>
    </Require>
  )
}

export default AWSIoTIntegration
